/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import axiosInstance, { axiosMultipart } from './axios';
import PaginationData from 'types/models/PaginationData';
import Event from 'types/models/Event';
import EventDetail from 'types/models/EventDetail';
import { parseImages } from './imageServices';
import Consumer from 'types/models/Consumer';
import { EventConsumptionReportPayloadData } from 'types/forms/EventConsumptionsReportFormProps';

export const getEvents = async (venueId: string, page?: number): Promise<PaginationData<Event> | null> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `venues/${venueId}/events?page=${page ?? 1}`,
    });
    return {
      currentPage: paginationData.current_page,
      data: parseEvents(paginationData.data),
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const storeEvent = async (id: string, formData: FormData): Promise<Event | null> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: `venues/${id}/events`,
      data: formData,
    });
    return parseEvent(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getEvent = async (eventId: string): Promise<Event | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `events/${eventId}`,
    });
    return parseEvent(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const updateEvent = async (eventId: string, formData: FormData): Promise<Event | null> => {
  try {
    const { data } = await axiosMultipart({
      method: 'post',
      url: `events/${eventId}?_method=put`,
      data: formData,
    });
    return parseEvent(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const deleteEvent = async (eventId: string): Promise<Event | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'delete',
      url: `events/${eventId}`,
    });
    return parseEvent(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getEventConsumptionsReport = async (
  formData: EventConsumptionReportPayloadData,
  page?: number,
): Promise<PaginationData<Consumer> | Blob | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `events/reporting`,
      data: {
        ...formData,
        page,
      },
    });

    const isDownload = formData.download === true;

    if (isDownload) {
      return data;
    }
    return {
      currentPage: data.current_page,
      data: parseConsumers(data.data),
      perPage: data.per_page,
      total: data.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getFailedJobs = async (page?: number): Promise<PaginationData<any>> => {
  try {
    const { data: paginationData } = await axiosInstance({
      method: 'get',
      url: `offline/failed-jobs?page=${page ?? 1}`,
    });

    return {
      currentPage: paginationData.current_page,
      data: paginationData.data,
      perPage: paginationData.per_page,
      total: paginationData.total,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const reProcessOfflineJobById = async (failedJobId: number): Promise<any> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `offline/failed-jobs/${failedJobId}/reprocess`,
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const downloadEventConsumptionsReport = async (
  formData: EventConsumptionReportPayloadData,
  page?: number,
): Promise<PaginationData<Consumer> | Blob | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: `events/reporting`,
      data: {
        ...formData,
        page,
      },
      responseType: 'blob',
    });

    const isDownload = formData.download === true;

    if (isDownload) {
      return data;
    }
    return {
      currentPage: data.current_page,
      data: parseConsumers(data.data),
      perPage: data.per_page,
      total: data.total,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getAllEvents = async ({ venueId }: { venueId: string }): Promise<Event[] | null> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `events/${venueId}/all`,
    });
    return parseEvents(data);
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const parseEvents = (events: any): Event[] => {
  return events.map((event: any) => parseEvent(event));
};

export const parseEvent = (event: any): Event => {
  return {
    id: event.id,
    name: event.name,
    eventStart: event.event_start,
    eventEnd: event.event_end,
    detail: event.detail ? parseEventDetail(event.detail) : undefined,
    images: event.images ? parseImages(event.images) : undefined,
  };
};

const parseEventDetail = (detail: any): EventDetail => {
  return {
    id: detail.id,
    description: detail.description,
  };
};

const parseConsumers = (consumers: any): Consumer[] => {
  return consumers.map((consumer: any) => parseConsumer(consumer));
};

export const parseConsumer = (consumer: any): Consumer => {
  return {
    id: consumer.id,
    documentNumber: consumer.document_number,
    name: consumer.name,
    lastName: consumer.last_name,
    eventName: consumer.event_name,
    consumedCount: consumer.consumed_count,
  };
};
